import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { CheckCircle, AlertCircle } from 'lucide-react';

const ClaimAccount = () => {
  const navigate = useNavigate();
  const [whopUsername, setWhopUsername] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleClaimAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!whopUsername || !email) {
        throw new Error('Please fill in all fields');
      }

      // Find account with temp email matching the username
      const tempEmail = `${whopUsername.toLowerCase().replace(/\s+/g, '_')}@whop.temp`;
      
      const { data: profile, error: findError } = await supabase
        .from('user_profiles')
        .select('*')
        .or(`email.eq.${tempEmail},full_name.ilike.%${whopUsername}%`)
        .single();

      if (findError || !profile) {
        throw new Error('Could not find your Whop purchase. Make sure you entered the exact username from your Whop account.');
      }

      console.log('Found profile to claim:', profile.id);

      // Check if this email is already in use
      const { data: existingEmail } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', email)
        .single();

      if (existingEmail && existingEmail.id !== profile.id) {
        throw new Error('This email is already registered. Please use a different email or sign in.');
      }

      // Update the profile with real email
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          email: email,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      // Update auth user email
      const { error: authError } = await supabase.auth.admin.updateUserById(
        profile.id,
        { email: email }
      );

      if (authError) {
        console.error('Auth update error:', authError);
      }

      // Send magic link to new email
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/app/industry-selection`
        }
      });

      if (otpError) throw otpError;

      setSuccess(true);

    } catch (err) {
      console.error('Claim account error:', err);
      setError(err.message || 'Failed to claim account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Account Claimed!</h2>
            <p className="text-muted-foreground mb-6">
              Check your email at <strong>{email}</strong> for a magic link to sign in.
            </p>
            <Button onClick={() => navigate('/authentication')} fullWidth>
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Claim Your Account</h1>
            <p className="text-muted-foreground">
              Link your Whop purchase to your email address
            </p>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">
                  Find your Whop username:
                </p>
                <ol className="text-blue-600 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>Go to whop.com</li>
                  <li>Click your profile in top right</li>
                  <li>Your username is shown there</li>
                </ol>
              </div>
            </div>
          </div>

          <form onSubmit={handleClaimAccount} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Whop Username
              </label>
              <Input
                type="text"
                value={whopUsername}
                onChange={(e) => setWhopUsername(e.target.value)}
                placeholder="e.g. bumfusion"
                required
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter your exact Whop username (not email)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Your Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                We'll send your login link here
              </p>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button type="submit" fullWidth disabled={loading}>
              {loading ? 'Claiming Account...' : 'Claim Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/authentication')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimAccount;
